import React, { createContext, useContext, useState, ReactNode } from "react";
import CreateVisitDialog from "../components/Dialogs/CreateVisitDialog";
import { Visit } from "@visitproof/types";

interface CreateVisitDialogContextType {
  openDialog: (options: {
    title?: string;
    visit: Partial<Visit>;
    onVisitCreated?: (visitId: string) => void;
  }) => void;
}

const CreateVisitDialogContext = createContext<
  CreateVisitDialogContextType | undefined
>(undefined);

export const useCreateVisitDialog = () => {
  const context = useContext(CreateVisitDialogContext);
  if (!context) {
    throw new Error(
      "useCreateVisitDialog must be used within a CreateVisitDialogProvider",
    );
  }
  return context;
};

interface Props {
  children: ReactNode;
}

export const CreateVisitDialogProvider: React.FC<Props> = ({ children }) => {
  const [dialogProps, setDialogProps] = useState<{
    title?: string;
    visit: Partial<Visit>;
    onVisitCreated?: (visitId: string) => void;
  } | null>(null);

  const openDialog = (options: {
    title?: string;
    visit: Partial<Visit>;
    onVisitCreated?: (visitId: string) => void;
  }) => {
    setDialogProps(options);
  };

  const handleClose = () => {
    setDialogProps(null);
  };

  return (
    <CreateVisitDialogContext.Provider value={{ openDialog }}>
      {children}
      {dialogProps && (
        <CreateVisitDialog
          title={dialogProps.title || "Create Visit"}
          visit={dialogProps.visit}
          onClose={handleClose}
          onVisitCreated={(visitId: string) => {
            if (dialogProps.onVisitCreated) {
              dialogProps.onVisitCreated(visitId);
            }
            handleClose();
          }}
        />
      )}
    </CreateVisitDialogContext.Provider>
  );
};
